import { NgClass } from '@angular/common'
import { Component, computed, inject, input, output, signal } from '@angular/core'
import { DropdownComponent } from '@app/v2/shared/components/dropdown/dropdown.component'
import { DropdownOption } from '@app/v2/shared/components/dropdown/types/dropdown-option.type'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { DeleteModalComponent } from '@app/v2/shared/components/upload/components/delete-modal/delete-modal.component'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { BillingIcons } from '../../../../models/organization-settings.model'
import { OrganizationPaymentMethod } from '../../../../types/organization-settings.type'

export type BillingPaymentMethod = OrganizationPaymentMethod

@Component({
  selector: 'app-billing-payment-cards-modal',
  standalone: true,
  imports: [ModalComponent, TranslateModule, DropdownComponent, NgClass, DeleteModalComponent],
  templateUrl: './payment-cards-modal.component.html',
  styleUrl: './payment-cards-modal.component.scss'
})
export class PaymentCardsModalComponent {
  private readonly _billingIcons = BillingIcons.getIconsConfiguration()

  public readonly modalId = input.required<string>()
  public readonly addPaymentCardModalId = input.required<string>()
  public readonly headerIcon = input.required<string>()
  public readonly paymentMethods = input<BillingPaymentMethod[]>([])
  /** When true, default card actions are hidden (per product rule for Pro plans). */
  public readonly isProPlan = input(false)
  /** When false, card row actions and add-card entry points are hidden (view-only modal). */
  public readonly canEditPaymentCards = input(true)
  /** Stripe payment method ID currently being deleted, if any. */
  public readonly removingPaymentMethodId = input<string | null>(null)
  public readonly getCardBrandIconUrl = input<(brand?: string) => string>(() => '')
  public readonly formatCardBrand = input<(brand?: string) => string>(() => '')
  public readonly formatCardExpiry = input<(expMonth?: string | number, expYear?: string | number) => string>(() => '')

  public readonly setDefault = output<BillingPaymentMethod>()
  public readonly remove = output<BillingPaymentMethod>()

  /** Saved card awaiting user confirmation before removal. */
  public readonly paymentMethodPendingRemoval = signal<BillingPaymentMethod | null>(null)

  public readonly removePaymentCardConfirmModalId = computed(() => `${this.modalId()}-remove-confirm`)

  public readonly removePaymentCardConfirmMessage = computed(() => {
    const last4 = this.paymentMethodPendingRemoval()?.last4 ?? ''
    return this._translateService.instant('v2.orgSettings.billing.removePaymentCardConfirmMessage', { last4 })
  })

  private readonly _modalService = inject(ModalService)
  private readonly _translateService = inject(TranslateService)

  /**
   * Close the payment cards modal.
   */
  public close(): void {
    if (this._modalService.isModalOpen(this.modalId())) {
      this._modalService.toggleModal(this.modalId())
    }
  }

  /**
   * Open the add payment card modal.
   */
  public openAddNew(): void {
    this._modalService.toggleModal(this.addPaymentCardModalId())
  }

  /**
   * Whether the remove-payment-card confirmation modal is open.
   * @returns {boolean} True when the confirm modal is visible
   */
  public isRemoveConfirmModalOpen(): boolean {
    return this._modalService.isModalOpen(this.removePaymentCardConfirmModalId())
  }

  /**
   * Open the confirmation modal before removing a saved payment card.
   * @param {BillingPaymentMethod} method Saved payment method to remove
   */
  public openRemoveConfirm(method: BillingPaymentMethod): void {
    this.paymentMethodPendingRemoval.set(method)
    this._modalService.toggleModal(this.removePaymentCardConfirmModalId())
  }

  /**
   * Close the remove-payment-card confirmation modal and clear the pending card.
   */
  public closeRemoveConfirm(): void {
    if (this._modalService.isModalOpen(this.removePaymentCardConfirmModalId())) {
      this._modalService.toggleModal(this.removePaymentCardConfirmModalId())
    }

    this.paymentMethodPendingRemoval.set(null)
  }

  /**
   * Confirm removal of the pending saved payment card.
   */
  public confirmRemovePaymentCard(): void {
    const method = this.paymentMethodPendingRemoval()
    this.paymentMethodPendingRemoval.set(null)

    if (method) {
      this.remove.emit(method)
    }
  }

  /**
   * Whether the given payment method is currently being deleted.
   * @param {BillingPaymentMethod} method Saved payment method
   * @returns {boolean} True while the delete API call is in progress
   */
  public isRemoving(method: BillingPaymentMethod): boolean {
    return this.removingPaymentMethodId() === method.id
  }

  /**
   * Whether the given payment method is the organization default.
   * @param {BillingPaymentMethod} method Saved payment method
   * @returns {boolean} True when the card is default
   */
  public isDefault(method: BillingPaymentMethod): boolean {
    return !!method?.is_default
  }

  /**
   * Whether card actions should be shown for the given payment method.
   * @param {BillingPaymentMethod} method Saved payment method
   * @returns {boolean} True when set-default/remove actions are available
   */
  public shouldShowActions(method: BillingPaymentMethod): boolean {
    if (!this.canEditPaymentCards()) return false
    if (this.isProPlan() && this.isDefault(method)) return false

    return true
  }

  /**
   * Whether the given payment method is expired.
   * @param {BillingPaymentMethod} method Saved payment method
   * @returns {boolean} True when the card is expired
   */
  public isExpired(method: BillingPaymentMethod): boolean {
    const m = Number(method?.exp_month)
    const y = Number(method?.exp_year)
    if (!m || !y) return false

    const now = new Date()
    const exp = new Date(y, m, 0, 23, 59, 59, 999)
    return exp.getTime() < now.getTime()
  }

  /**
   * Build dropdown menu options for a saved payment method row.
   * @param {BillingPaymentMethod} method Saved payment method
   * @returns {DropdownOption[]} Menu options for the card row
   */
  public getCardMenuOptions(method: BillingPaymentMethod): DropdownOption[] {
    if (!this.shouldShowActions(method)) return []

    const setDefaultIcon = this._billingIcons.setAsDefault as DropdownOption['prefixIcon']

    const trashIcon = `<svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M7.5 2.5H12.5M2.5 5H17.5M15.8333 5L15.2546 15.128C15.2067 15.9659 14.5129 16.625 13.6738 16.625H6.32622C5.48708 16.625 4.79329 15.9659 4.74538 15.128L4.16667 5M8.33333 8.75V13.75M11.6667 8.75V13.75" stroke="#DC2626" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`

    if (this.isExpired(method)) {
      return [
        {
          key: 'remove',
          value: this._translateService.instant('v2.orgSettings.billing.removePaymentCard'),
          prefixIcon: trashIcon,
          textColorClass: 'tw-text-red-600'
        }
      ]
    }

    if (this.isDefault(method)) {
      return [
        {
          key: 'remove',
          value: this._translateService.instant('v2.orgSettings.billing.removePaymentCard'),
          prefixIcon: trashIcon,
          textColorClass: 'tw-text-red-600'
        }
      ]
    }

    return [
      {
        key: 'set-default',
        value: this._translateService.instant('v2.orgSettings.billing.setAsDefault'),
        prefixIcon: setDefaultIcon
      },
      {
        key: 'remove',
        value: this._translateService.instant('v2.orgSettings.billing.removePaymentCard'),
        prefixIcon: trashIcon,
        textColorClass: 'tw-text-red-600'
      }
    ]
  }

  /**
   * Handle a card row dropdown selection.
   * @param {DropdownOption} option Selected dropdown option
   * @param {BillingPaymentMethod} method Saved payment method
   */
  public onMenuOptionClicked(option: DropdownOption, method: BillingPaymentMethod): void {
    if (!option?.key) return

    if (option.key === 'set-default') this.setDefault.emit(method)
    if (option.key === 'remove') this.openRemoveConfirm(method)
  }
}
